/**
 * Map Block
 * Displays a location map using OpenStreetMap or Google Maps embed.
 */

import { registerBlockRenderer, getBlockRendererContext } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import type { MapData } from '../types';

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidMapData(data: unknown): data is MapData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have either address or coordinates
  const hasAddress = typeof d.address === 'string' && d.address.length > 0;
  const hasCoords = typeof d.lat === 'number' && typeof d.lng === 'number';

  if (!hasAddress && !hasCoords) return false;

  // Validate coordinates if present
  if (hasCoords) {
    const lat = d.lat as number;
    const lng = d.lng as number;
    if (lat < -90 || lat > 90) return false;
    if (lng < -180 || lng > 180) return false;
  }

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderMap(data: MapData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('map');

  const lat = data.lat;
  const lng = data.lng;
  const zoom = data.zoom || 15;
  const height = data.height || '300px';
  const provider = data.provider || 'static';

  let html = '';
  html += `<div class="pms-map-container" style="height: ${escapeHtml(height)}">`;

  if (provider === 'embed' && lat !== undefined && lng !== undefined) {
    // Interactive Google Maps embed
    html += `
      <iframe
        class="pms-map-iframe"
        src="https://maps.google.com/maps?q=${lat},${lng}&z=${zoom}&output=embed"
        title="Map location"
        frameborder="0"
        allowfullscreen
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
      ></iframe>
    `;
  } else if (lat !== undefined && lng !== undefined) {
    // Static map using OpenStreetMap (no API key required)
    const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`;
    html += `
      <iframe
        class="pms-map-iframe"
        src="${escapeHtml(osmUrl)}"
        title="Map location"
        frameborder="0"
        loading="lazy"
      ></iframe>
    `;
  } else if (data.address) {
    // Address-based static map
    const encodedAddress = encodeURIComponent(data.address);
    html += `
      <iframe
        class="pms-map-iframe"
        src="https://maps.google.com/maps?q=${encodedAddress}&output=embed"
        title="Map: ${escapeHtml(data.address)}"
        frameborder="0"
        allowfullscreen
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
      ></iframe>
    `;
  }

  html += '</div>';

  // Address and directions link
  if (data.address) {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(data.address)}`;
    html += `
      <div class="pms-map-footer">
        <div class="pms-map-address">${escapeHtml(data.address)}</div>
        <a href="${escapeHtml(directionsUrl)}" target="_blank" rel="noopener noreferrer" class="pms-map-directions">
          <svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M21.71 11.29l-9-9c-.39-.39-1.02-.39-1.41 0l-9 9c-.39.39-.39 1.02 0 1.41l9 9c.39.39 1.02.39 1.41 0l9-9c.39-.38.39-1.01 0-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z"/></svg>
          Get Directions
        </a>
      </div>
    `;
  }

  wrapper.innerHTML = html;

  // Vertical signature — decorative accents around map
  const vertical = getBlockRendererContext().vertical;

  if (vertical === 'saas' || vertical === 'tech') {
    // Coordinate readout overlay
    const coords = document.createElement('div');
    coords.className = 'pms-map-coords-overlay';
    if (data.lat !== undefined && data.lng !== undefined) {
      coords.textContent = `${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`;
    } else {
      coords.textContent = 'LOCATION';
    }
    wrapper.querySelector('.pms-map-container')?.appendChild(coords);
  }

  if (vertical === 'restaurant') {
    // "FIND US" banner on map
    const banner = document.createElement('div');
    banner.className = 'pms-map-find-banner';
    banner.textContent = 'FIND US';
    wrapper.querySelector('.pms-map-container')?.appendChild(banner);
  }

  if (vertical === 'real_estate') {
    // Compass rose overlay
    const compass = document.createElement('div');
    compass.className = 'pms-map-compass-rose';
    wrapper.querySelector('.pms-map-container')?.appendChild(compass);

    // Corner survey marks
    const marks = document.createElement('div');
    marks.className = 'pms-map-survey-marks';
    wrapper.querySelector('.pms-map-container')?.appendChild(marks);
  }

  if (vertical === 'healthcare') {
    // Location pin with cross
    const pin = document.createElement('div');
    pin.className = 'pms-map-care-pin';
    wrapper.querySelector('.pms-map-container')?.appendChild(pin);
  }

  if (vertical === 'finance' || vertical === 'fintech') {
    // "BRANCH LOCATION" label
    const label = document.createElement('div');
    label.className = 'pms-map-branch-label';
    label.textContent = 'BRANCH';
    wrapper.querySelector('.pms-map-container')?.appendChild(label);
  }

  if (vertical === 'automotive') {
    // Speedometer-style pin
    const pin = document.createElement('div');
    pin.className = 'pms-map-dealer-pin';
    wrapper.querySelector('.pms-map-container')?.appendChild(pin);
  }

  if (vertical === 'portfolio') {
    // Artboard corner marks
    const corners = document.createElement('div');
    corners.className = 'pms-map-artboard-frame';
    wrapper.querySelector('.pms-map-container')?.appendChild(corners);
  }

  if (vertical === 'professional_services') {
    // Office location badge
    const badge = document.createElement('div');
    badge.className = 'pms-map-office-badge';
    badge.textContent = 'OFFICE';
    wrapper.querySelector('.pms-map-container')?.appendChild(badge);
  }

  if (vertical === 'ecommerce') {
    // Store locator label
    const label = document.createElement('div');
    label.className = 'pms-map-store-label';
    label.textContent = 'STORE';
    wrapper.querySelector('.pms-map-container')?.appendChild(label);
  }

  if (vertical === 'agency') {
    // Agency HQ label
    const label = document.createElement('div');
    label.className = 'pms-map-agency-label';
    label.textContent = 'HQ';
    wrapper.querySelector('.pms-map-container')?.appendChild(label);
  }

  if (vertical === 'education') {
    // Campus map label
    const label = document.createElement('div');
    label.className = 'pms-map-campus-label';
    label.textContent = 'CAMPUS MAP';
    wrapper.querySelector('.pms-map-container')?.appendChild(label);
  }

  if (vertical === 'nonprofit') {
    // Location heart indicator
    const heart = document.createElement('div');
    heart.className = 'pms-map-location-heart';
    heart.innerHTML = '\u2665';
    wrapper.querySelector('.pms-map-container')?.appendChild(heart);
  }

  if (vertical === 'legal') {
    // Office plaque label
    const plaque = document.createElement('div');
    plaque.className = 'pms-map-office-plaque';
    plaque.textContent = 'OFFICE';
    wrapper.querySelector('.pms-map-container')?.appendChild(plaque);
  }

  // Dental: clinic location plaque
  if (vertical === 'dental') {
    const plaque = document.createElement('div');
    plaque.className = 'pms-map-clinic-plaque';
    plaque.textContent = 'CLINIC';
    wrapper.prepend(plaque);
  }

  // Fun: hacker base label
  if (vertical === 'fun') {
    const label = document.createElement('div');
    label.className = 'pms-map-base-label';
    label.textContent = 'BASE';
    wrapper.prepend(label);
  }

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('map', (data: unknown, container: HTMLElement) => {
  if (!isValidMapData(data)) {
    console.warn('[map] Invalid data:', data);
    const wrapper = createBlockWrapper('map');
    wrapper.innerHTML = '<div class="pms-map-error">Invalid location data - provide address or coordinates</div>';
    container.appendChild(wrapper);
    return;
  }

  renderMap(data, container);
});
